import { ref, computed, inject, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import SpinnerService from '@/services/spinner.service';
import { RESPONSE_CODE } from '@/constants/ApiConstants';
import { configurationKey } from '@/injections';
import { ORDER_TYPE_HOME_ROUTE_NAME } from '@/constants/AppConstants';

const lastVisitedOrderType = ref<any>(null);

export default function useOrderTypes() {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const $configuration = inject(configurationKey);

  const orderTypeNoLongerValid = ref(false);
  const orderType = ref<any>(null);
  const loading = ref(false);
  const loadFailedError = ref<any>(null);

  const getCartOrderType = computed(() => store.getters['cart/getCartOrderType']);
  const getUser = computed(() => store.getters['user/getUser']);
  const isAcoMode = computed(() => store.getters['user/isCustomerModeScan']);

  const reloadCart = () => store.dispatch('cart/reload');
  const setActiveCart = (payload: any) => store.dispatch('cart/setActiveCart', payload);

  const orderTypeObjectFromId = (orderTypeIdParam: string | null) => {
    if (!orderTypeIdParam) return null;

    let orderTypeId = orderTypeIdParam;
    if (orderTypeId.startsWith('WOT-')) {
      orderTypeId = orderTypeId.substring(4);
    }

    return $configuration?.orderTypes?.find((ot: any) => ot.id === orderTypeId);
  };

  const findItemGroupInMenu = (menu: any, itemGroupId: string): any => {
    if (!menu || !itemGroupId) return undefined;

    let foundMenuItem: any;
    Object.values(menu).some((menuItem: any) => {
      if (menuItem === itemGroupId) {
        foundMenuItem = menuItem;
        return true;
      }
      if (typeof menuItem === 'object') {
        foundMenuItem = findItemGroupInMenu(menuItem, itemGroupId);
        return foundMenuItem !== undefined;
      }
      return false;
    });
    return foundMenuItem;
  };

  const visibleOrderTypes = computed(() => {
    return $configuration?.orderTypes?.filter(
      (ot: any) => !ot.hidden && (!ot.giftCard || $configuration.allowEGiftCards),
    );
  });

  const activeOrderType = computed(() => {
    return orderTypeObjectFromId(getCartOrderType.value);
  });

  const orderTypeFromCurrentRoute = computed(() => {
    const routeParam = route.params.id as string;
    return (
      route.name === ORDER_TYPE_HOME_ROUTE_NAME &&
      $configuration?.orderTypes?.find(
        (ot: any) =>
          ot.id === routeParam ||
          decodeURIComponent(ot.urlEncodedSlug ?? '').toLowerCase() === routeParam?.toLowerCase(),
      )
    );
  });

  watch(
    orderTypeFromCurrentRoute,
    value => {
      if (value) lastVisitedOrderType.value = value;
    },
    { immediate: true },
  );

  const currentOrderType = computed(
    () => orderTypeFromCurrentRoute.value || lastVisitedOrderType.value || activeOrderType.value,
  );

  const orderTypesMenu = computed(() => {
    const mappedOrderTypes = visibleOrderTypes.value?.map((ot: any) => {
      return {
        children: ot.menu ? ot.menu.children : null,
        name: ot.name,
        id: ot.id,
      };
    });

    const menu = {
      children: mappedOrderTypes,
      name: 'Order Types',
    };

    return menu;
  });

  const searchableOrderTypes = computed(() => {
    return visibleOrderTypes.value
      ?.filter((ot: any) => !ot.giftCard)
      ?.map((ot: any) => (ot.id ? { ...ot, id: `WOT-${ot.id}` } : undefined));
  });

  const orderTypeHomeForOrderType = (ot: any) => {
    if (!ot) return '/';
    return `/orders/${ot.id}`;
  };

  const setActiveOrderType = async (orderTypeParam: any) => {
    if (loading.value) return;

    loading.value = true;
    SpinnerService.open();

    try {
      if (orderTypeParam) {
        const response = await axios.get(`/api/ot/${orderTypeParam.id}`);
        orderType.value = response.data;
        orderTypeNoLongerValid.value = false;
        await reloadCart();

        if (orderType.value.giftCard) {
          await setActiveCart({ orderTypeId: orderType.value.id });
        }

        AnalyticsService.track(TrackableEventConstants.OrderTypeSwitched, {
          id: orderTypeParam.id,
          customerId: getUser.value?.sub,
          name: orderTypeParam.name,
          ads: orderTypeParam.ads,
        });
      }
    } catch (error) {
      loadFailedError.value = error;
      console.error(error);

      if (error.response?.data?.code === RESPONSE_CODE.CODE_ORDER_TYPE_REMOVED) {
        orderTypeNoLongerValid.value = true;
        (ToastService as any).open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.response?.data?.message,
          },
          timeout: 7000,
        });
        throw new Error('Aborting due to invalid order type');
      }
    } finally {
      loading.value = false;
      SpinnerService.close();
    }
  };

  const isItemEligibleForOrderType = (item: any, ot: any) => {
    if (!item || !ot || !$configuration?.orderTypes) return false;

    const menu = ot.menu;
    const itemGroupIds = item.itemGroups;
    return itemGroupIds?.find((itemGroupId: string) => findItemGroupInMenu(menu, itemGroupId));
  };

  const eligibleOrderTypesForItem = (item: any) => {
    const eligibleOrderTypes: any[] = [];

    visibleOrderTypes.value?.forEach((ot: any) => {
      if (isItemEligibleForOrderType(item, ot)) {
        eligibleOrderTypes.push(ot);
      }
    });

    return eligibleOrderTypes;
  };

  const eligibleInactiveOrderTypesForItem = (item: any) => {
    return eligibleOrderTypesForItem(item).filter(
      (ot: any) => ot.id !== activeOrderType.value?.id && !ot.listOnly,
    );
  };

  const navigateToOrderTypeHome = (orderTypeId: string) => {
    if (!orderTypeId) return Promise.resolve();

    return router.push({
      name: 'Shopping by Order Type',
      params: { id: orderTypeId },
    });
  };

  const isItemExclusivelyListOnly = (item: any) => {
    if (isAcoMode.value) {
      return false;
    }
    if (!$configuration?.cartEnabled) {
      return true;
    }
    const orderTypes = eligibleOrderTypesForItem(item);
    for (const ot of orderTypes) {
      if (!ot?.listOnly) {
        return false;
      }
    }
    return true;
  };

  const isItemListOnly = (item: any) => {
    if (isAcoMode.value) {
      return false;
    }
    if (!$configuration?.cartEnabled) {
      return true;
    }
    if (!$configuration?.orderTypesEnabled) {
      return false;
    }
    if (isItemEligibleForOrderType(item, activeOrderType.value)) {
      if (activeOrderType.value?.listOnly) {
        return true;
      }
      return false;
    }
    if (activeOrderType.value?.listOnly) {
      return true;
    }
    if (isItemExclusivelyListOnly(item)) {
      return true;
    }
    return false;
  };

  return {
    orderTypeNoLongerValid,
    orderType,
    loading,
    loadFailedError,
    getCartOrderType,
    getUser,
    isAcoMode,
    visibleOrderTypes,
    activeOrderType,
    currentOrderType,
    orderTypesMenu,
    searchableOrderTypes,
    reloadCart,
    setActiveCart,
    orderTypeHomeForOrderType,
    setActiveOrderType,
    orderTypeObjectFromId,
    isItemEligibleForOrderType,
    eligibleOrderTypesForItem,
    eligibleInactiveOrderTypesForItem,
    findItemGroupInMenu,
    navigateToOrderTypeHome,
    isItemListOnly,
    isItemExclusivelyListOnly,
  };
}
